import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmApi } from '../../api/crm';
import { queryKeys } from '../queryClient';
import type { CmsSection, Customer, CustomerPreferences } from '../../types/domain';

export function useCustomers() {
  return useQuery({ queryKey: queryKeys.customers, queryFn: () => crmApi.listCustomers() });
}

export function useCustomer360(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customer360(id ?? ''),
    queryFn: () => crmApi.getCustomer360(id!),
    enabled: !!id,
  });
}

export function useUpsertCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Customer> & { customerId?: string | null }) => crmApi.upsertCustomer(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.customers }),
  });
}

export function useUpdatePreferences(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: CustomerPreferences) => crmApi.updatePreferences(customerId, prefs),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.customer360(customerId) }),
  });
}

// ── CMS ──
// 30 s (was 5 min): an admin's "hide" / edit must show up on the storefront within moments, not after a coffee break.
export function useCmsPublic(page: string) {
  return useQuery({
    queryKey: queryKeys.cmsPublic(page),
    queryFn: () => crmApi.getPublicPage(page),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Which blocks of a page are shown and in what order — the storefront gates every section on this. */
export function useCmsLayout(page: string) {
  return useQuery({
    queryKey: queryKeys.cmsLayout(page),
    queryFn: () => crmApi.getPublicLayout(page),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Manager — make sure every default block of `page` has a row (the CMS admin page calls this when a page is opened). */
export function useEnsureCmsBlocks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (page: string) => crmApi.ensureBlocks(page),
    onSuccess: (res) => {
      if (res.created.length > 0) qc.invalidateQueries({ queryKey: ['cms'] });
    },
  });
}

export function useCmsSections(page?: string) {
  return useQuery({ queryKey: queryKeys.cmsSections(page), queryFn: () => crmApi.listSections(page) });
}

export function useSaveCmsSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (section: CmsSection): Promise<void> => {
      if (section.id) await crmApi.updateSection(section.id, section);
      else await crmApi.createSection(section);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cms'] }),
  });
}

export function useDeleteCmsSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => crmApi.removeSection(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cms'] }),
  });
}
